import {
  FEEDBACK_SUPPLIER_PROFILES,
  feedbackSalesHeader,
  hubbellFeedbackDivisionFromFileName,
  HUBBELL_FEEDBACK_MAX_SALES_FILES,
  HUBBELL_FEEDBACK_MEMBERSHIP_HEADER,
  type FeedbackSupplierProfile,
} from '@erp/rpc';

/**
 * Which Feedback input a dropped file is, from the little the browser
 * reads of it: its sheet names and first rows (`peekWorkbook`). The page never
 * parses the workbooks; the run does that on the server, from the same
 * headers, so the two agree on what a Results Workbook and a Customer Sales
 * Summary are.
 */

export interface SheetPeek {
  name: string;
  /** The first rows, as text per cell; empty for a sheet with no rows. */
  rows: string[][];
}

export interface WorkbookPeek {
  sheets: SheetPeek[];
}

export type HubbellFileKind = 'results' | 'sales' | 'unknown';

function normalizeHeader(cell: string): string {
  return cell.trim().toLowerCase();
}

/** Every wanted header appears in the row (case and padding aside), like the server's `hasHeaders`. */
export function hasHeaders(row: string[], headers: readonly string[]): boolean {
  const present = new Set(row.map(normalizeHeader));
  return headers.every((header) => present.has(normalizeHeader(header)));
}

function someRowHas(sheet: SheetPeek, headers: readonly string[]): boolean {
  return sheet.rows.some((row) => hasHeaders(row, headers));
}

export function classifyWorkbook(
  peek: WorkbookPeek,
  supplier: FeedbackSupplierProfile = FEEDBACK_SUPPLIER_PROFILES.hubbell,
): HubbellFileKind {
  // The results header sits within the supplier's first rows (Hubbell: the
  // first; ABB: under a UoM row), as the run reads it.
  const isResults = peek.sheets.some((sheet) =>
    sheet.rows
      .slice(0, supplier.resultsHeaderRows)
      .some((row) => hasHeaders(row, supplier.resultsHeader)),
  );
  if (isResults) return 'results';
  // The sales file's two tabs, on two different sheets, as the run reads
  // them (a sheet that matches the membership header is read as membership
  // only); the sales header sits under a title row.
  const membership = peek.sheets.findIndex((sheet) =>
    someRowHas(sheet, HUBBELL_FEEDBACK_MEMBERSHIP_HEADER),
  );
  const hasSales = peek.sheets.some(
    (sheet, index) =>
      index !== membership && someRowHas(sheet, feedbackSalesHeader(supplier)),
  );
  return membership >= 0 && hasSales ? 'sales' : 'unknown';
}

/** A file on the card: classified, still being read, or one the browser could not open. */
export interface DroppedHubbellFile {
  id: string;
  file: File;
  kind: HubbellFileKind | 'reading' | 'unreadable';
}

export type DropProblem =
  | 'second results workbook'
  | 'not a feedback input'
  | 'too many sales files'
  | 'names no division'
  | 'division already has a file'
  | 'unreadable';

export interface DropFileState extends DroppedHubbellFile {
  /** Why the file will be left out of the run; null for one that goes in. */
  problem: DropProblem | null;
  /** The tab letter of the Division a sales file names; null otherwise. */
  tab: string | null;
}

export interface DropState {
  files: DropFileState[];
  resultsWorkbook: File | null;
  salesFiles: File[];
  /** Tab letter → the sales file that names its Division. */
  salesFileByTab: ReadonlyMap<string, DropFileState>;
  /** Tab letters the user marked "no sales file": built with Sales 0. */
  noSalesFileTabs: readonly string[];
  /** A file is still being classified. */
  reading: boolean;
  /** One Results Workbook, all files read, and at least one Division to build. */
  ready: boolean;
}

type Claimed = { results: boolean; sales: number; tabs: ReadonlySet<string> };

/** Why a sales file is left out: past the cap, naming no Division, or a Division already covered. */
function salesProblem(
  claimed: Claimed,
  tab: string | null,
): DropProblem | null {
  if (claimed.sales >= HUBBELL_FEEDBACK_MAX_SALES_FILES)
    return 'too many sales files';
  if (tab === null) return 'names no division';
  return claimed.tabs.has(tab) ? 'division already has a file' : null;
}

function problemFor(
  entry: DroppedHubbellFile,
  claimed: Claimed,
  tab: string | null,
): DropProblem | null {
  switch (entry.kind) {
    case 'results':
      return claimed.results ? 'second results workbook' : null;
    case 'sales':
      return salesProblem(claimed, tab);
    case 'unknown':
      return 'not a feedback input';
    case 'unreadable':
      return 'unreadable';
    case 'reading':
      return null;
  }
}

/** The tab a sales file goes to: the Division its name starts with, if that Division has sales files. */
function salesFileTab(fileName: string): string | null {
  const division = hubbellFeedbackDivisionFromFileName(fileName);
  return division && division.hasSalesFile ? division.letter : null;
}

/**
 * What the drop amounts to: the first Results Workbook and one sales file
 * per Division go in, placed under the Division their name starts with;
 * anything else is flagged with why, in drop order, and left out. Divisions
 * marked "no sales file" build with Sales 0; one with a file is never marked.
 */
export function dropState(
  entries: DroppedHubbellFile[],
  noSalesFileTabs: readonly string[] = [],
): DropState {
  // What the drop has claimed so far: the one Results slot and the sales files' tabs.
  const claimed = { results: false, sales: 0, tabs: new Set<string>() };
  let resultsWorkbook: File | null = null;
  const salesFiles: File[] = [];
  const salesFileByTab = new Map<string, DropFileState>();
  const files = entries.map((entry): DropFileState => {
    const tab = entry.kind === 'sales' ? salesFileTab(entry.file.name) : null;
    const problem = problemFor(entry, claimed, tab);
    if (problem === null && entry.kind === 'results') {
      claimed.results = true;
      resultsWorkbook = entry.file;
    }
    const state: DropFileState = { ...entry, problem, tab };
    if (problem === null && entry.kind === 'sales' && tab !== null) {
      claimed.sales += 1;
      claimed.tabs.add(tab);
      salesFiles.push(entry.file);
      salesFileByTab.set(tab, state);
    }
    return state;
  });
  const reading = entries.some((entry) => entry.kind === 'reading');
  const noFile = noSalesFileTabs.filter((tab) => !salesFileByTab.has(tab));
  return {
    files,
    resultsWorkbook,
    salesFiles,
    salesFileByTab,
    noSalesFileTabs: noFile,
    reading,
    ready:
      !reading &&
      resultsWorkbook !== null &&
      (salesFileByTab.size > 0 || noFile.length > 0),
  };
}
